// Wirtschaftlichkeitsberechnung fuer PV-Projekte (client-seitig).
// Reine Berechnungslogik ohne Seiteneffekte.

/**
 * Berechnet die gewichtete EEG-Verguetung aus Stufentarifen.
 *
 * @param {number} kwp - Generatorleistung in kWp
 * @param {Array<{ab_kwp: number, ct_kwh: number}>} eegStufen - Verguetungsstufen aufsteigend
 * @returns {number} gewichtete Verguetung in ct/kWh
 */
export function berechneGewichteteEegVerguetung(kwp, eegStufen) {
  if (!Array.isArray(eegStufen) || eegStufen.length === 0 || !kwp || kwp <= 0) return 0;

  // Stufen nach ab_kwp aufsteigend sortieren
  const sorted = [...eegStufen].sort((a, b) => (a.ab_kwp || 0) - (b.ab_kwp || 0));

  let totalWeighted = 0;
  let remaining = kwp;

  for (let i = 0; i < sorted.length && remaining > 0; i++) {
    const lower = sorted[i].ab_kwp || 0;
    const upper = i + 1 < sorted.length ? sorted[i + 1].ab_kwp : Infinity;
    const rate = sorted[i].ct_kwh || 0;
    const band = Math.min(remaining, Math.max(0, upper - lower));
    totalWeighted += band * rate;
    remaining -= band;
  }

  return totalWeighted / kwp;
}

// Standard-EEG-Verguetungsstufen (ct/kWh) als Fallback, wenn ein Projekt keine
// eigenen Stufen (eeg_verguetungsstufen_json) mitbringt. Werte spiegeln exakt die
// bereits vorhandenen Tarife EEG_TARIF_1/EEG_TARIF_2 aus api/_lib/pvsolExtractor.js
// (dort in EUR/kWh; hier ×100 in ct/kWh). KEINE erfundenen Tarifwerte.
// EEG-Verguetung fuer Inbetriebnahme 1. August 2026 – 31. DEZEMBER 2026
// (Volleinspeisung). Am 16.09.2026 gegen die Veroeffentlichung der
// Bundesnetzagentur geprueft: Die Saetze stimmen, der Zeitraum stand hier
// aber einen Monat zu lang (vorher "31. Januar 2027"). Wer im Januar
// nachgesehen haette, waere von gueltigen Werten ausgegangen.
//
// WARUM Jahresende, obwohl die Degression halbjaehrlich laeuft (1. Februar /
// 1. August) — bitte nicht zurueck-"korrigieren": Zum 01.01.2027 aendert sich
// das EEG grundlegend, deshalb endet die Geltung dieser Saetze mit dem Jahr.
// Festlegung des Fachbereichs (21.09.2026), keine Ableitung aus dem Rhythmus.
// Ohne diesen Satz wurde die Stelle schon zweimal fuer einen Tippfehler
// gehalten.
//
// Diese Stufen sind nur der Rueckfall. Fuer neue Berechnungen bitte
// src/lib/eegVerguetung.js verwenden: dort liegen zusaetzlich die Saetze fuer
// Freiflaechen (§ 48 Abs. 1) und die Teileinspeisung, und die Bibliothek meldet
// selbst, wenn ihre Saetze ablaufen.
const DEFAULT_EEG_STUFEN_UNTER_100 = [
  { ab_kwp: 0, ct_kwh: 12.22 },
  { ab_kwp: 10, ct_kwh: 10.24 },
  { ab_kwp: 40, ct_kwh: 10.24 },
  { ab_kwp: 100, ct_kwh: 0 },
];
const DEFAULT_EEG_STUFEN_AB_100 = [
  { ab_kwp: 0, ct_kwh: 12.62 },
  { ab_kwp: 10, ct_kwh: 10.64 },
  { ab_kwp: 40, ct_kwh: 10.64 },
  { ab_kwp: 100, ct_kwh: 8.85 },
  { ab_kwp: 400, ct_kwh: 7.63 },
  { ab_kwp: 1000, ct_kwh: 0 },
];

/**
 * Liefert die passenden Standard-EEG-Stufen fuer eine kWp-Groesse.
 * < 100 kWp → Volleinspeisung; ab 100 kWp → Marktpraemie.
 * Rueckgabe ist eine Kopie, damit Aufrufer die Konstanten nicht mutieren.
 *
 * @param {number} kwp - Generatorleistung in kWp
 * @returns {Array<{ab_kwp:number, ct_kwh:number}>}
 */
export function getDefaultEegStufen(kwp) {
  const source = (Number(kwp) || 0) >= 100 ? DEFAULT_EEG_STUFEN_AB_100 : DEFAULT_EEG_STUFEN_UNTER_100;
  return source.map((s) => ({ ...s }));
}

/**
 * Berechnet alle wirtschaftlichen Kennzahlen fuer ein PV-Projekt.
 *
 * @param {Object} params
 * @param {number} params.netzeinspeisung - kWh/Jahr Netzeinspeisung
 * @param {number} params.eegVerguetung - ct/kWh gewichtete EEG-Verguetung
 * @param {Array}  [params.eegStufen] - [{ab_kwp, ct_kwh}] EEG-Verguetungsstufen
 * @param {number} params.kwp - kWp Generatorleistung
 * @param {number} params.anlagenpreis - Anlagenpreis in EUR (Technik ohne Pacht)
 * @param {string} params.pachtart - 'einmal' | 'jaehrlich' | ''
 * @param {number} params.pachtkosten - Pachtkosten in EUR (einmalig oder jaehrlich)
 * @param {number} params.opex - Betriebskosten in EUR/Jahr (Wartung, Versicherung etc.)
 * @param {number} [params.betrachtungszeitraum=20] - Betrachtungszeitraum in Jahren
 * @param {number} [params.degradation=0.5] - Ertragsminderung in % pro Jahr
 * @param {number} [params.opexSteigerung=1.0] - Steigerung der Betriebskosten in % pro Jahr
 * @returns {Object} Berechnete Kennzahlen
 */
export function berechneWirtschaftlichkeit(params) {
  const {
    netzeinspeisung = 0,
    kwp = 0,
    anlagenpreis = 0,
    pachtart = '',
    pachtkosten = 0,
    opex = 0,
    betrachtungszeitraum = 20,
    degradation = 0.5,
    opexSteigerung = 1.0,
  } = params;

  // EEG-Verguetung bestimmen: expliziter Wert oder aus Stufen berechnet
  let eegVerguetung = params.eegVerguetung || 0;
  if (!eegVerguetung && Array.isArray(params.eegStufen) && params.eegStufen.length > 0 && kwp > 0) {
    const aktiveStufen = params.eegStufen.filter(s => s.ct_kwh > 0);
    eegVerguetung = berechneGewichteteEegVerguetung(kwp, aktiveStufen);
  }

  const pachtEinmal = pachtart === 'einmal' ? pachtkosten : 0;
  const pachtJaehrlich = pachtart === 'jaehrlich' ? pachtkosten : 0;

  // Gesamtpreis = Anlagenpreis + einmalige Pacht
  const turnkey_kaufpreis = anlagenpreis + pachtEinmal;
  const turnkey_preis_kwp = kwp > 0 ? turnkey_kaufpreis / kwp : 0;

  // Investition in Jahr 1
  const investitionJahr1 = turnkey_kaufpreis;

  // Jaehrliche Betriebskosten (Basisjahr), eskalieren mit opexSteigerung % pro Jahr
  const jaehrlicheBetriebskosten = opex + pachtJaehrlich;
  const betriebskostenImJahr = (jahr) => jaehrlicheBetriebskosten * Math.pow(1 + opexSteigerung / 100, jahr - 1);
  const ertragImJahr = (jahr) => netzeinspeisung * Math.pow(1 - degradation / 100, jahr - 1);

  // Cashflow-Tabelle aufbauen
  const cashflowTable = [];
  let kumuliert = 0;
  let totalErtragKwh = 0;
  let totalBetriebskosten = 0;
  let nettoErloesOperativ = 0;

  for (let jahr = 1; jahr <= betrachtungszeitraum; jahr++) {
    const jahresErtragKwh = ertragImJahr(jahr);
    const erloes = jahresErtragKwh * eegVerguetung / 100; // ct/kWh -> EUR

    const investition = jahr === 1 ? investitionJahr1 : 0;
    const betriebskosten = betriebskostenImJahr(jahr);
    const jaehrlich = erloes - betriebskosten - investition;
    kumuliert += jaehrlich;

    totalErtragKwh += jahresErtragKwh;
    totalBetriebskosten += betriebskosten;
    nettoErloesOperativ += erloes - betriebskosten;

    cashflowTable.push({
      jahr,
      ertragKwh: Math.round(jahresErtragKwh),
      investition,
      betriebskosten,
      erloes,
      jaehrlich,
      kumuliert,
    });
  }

  // Jahreserloese (Jahr 1)
  const jahreserloes = netzeinspeisung * eegVerguetung / 100;

  // Amortisationszeit mit Interpolation. Wird der kumulierte Cashflow innerhalb des
  // Horizonts nicht positiv, mit derselben Formel weiterrechnen (max. 60 Jahre).
  let payback = null;
  let paybackKumuliert = 0;
  for (let jahr = 1; jahr <= 60 && payback === null; jahr++) {
    const prev = paybackKumuliert;
    const erloes = ertragImJahr(jahr) * eegVerguetung / 100;
    paybackKumuliert += erloes - betriebskostenImJahr(jahr) - (jahr === 1 ? investitionJahr1 : 0);
    if (paybackKumuliert >= 0) {
      if (jahr === 1) {
        payback = 1;
      } else {
        const diff = paybackKumuliert - prev;
        payback = diff !== 0 ? jahr + (-prev / diff) : jahr;
      }
    }
  }

  // Stromgestehungskosten in ct/kWh
  // Gesamtkosten = Investition + Summe der eskalierten Betriebskosten
  const gesamtKosten = investitionJahr1 + totalBetriebskosten;
  const stromgestehungskosten = totalErtragKwh > 0
    ? (gesamtKosten / totalErtragKwh) * 100 // EUR -> ct
    : null;

  // NPV (Kapitalwert) bei 3% Diskontierungszins
  const diskontZins = 0.03;
  let kapitalwert = 0;
  for (let i = 0; i < cashflowTable.length; i++) {
    kapitalwert += cashflowTable[i].jaehrlich / Math.pow(1 + diskontZins, i + 1);
  }

  // Netto-Erloes ueber Betrachtungszeitraum: operative Summe (Erloes - Betriebskosten)
  // OHNE Investition; der Invest-Effekt steckt in IRR/Amortisation/Kapitalwert.
  const netto_erloes_25j = nettoErloesOperativ;

  // IRR (Gesamtkapitalrendite) via Newton-Raphson
  const irr = berechneIRR(cashflowTable, investitionJahr1);

  // Rendite p.a. — durchschnittlicher jährlicher Netto-Cashflow / Investition
  const rendite = investitionJahr1 > 0 && betrachtungszeitraum > 0
    ? (nettoErloesOperativ / betrachtungszeitraum / investitionJahr1) * 100
    : null;

  return {
    turnkey_kaufpreis,
    turnkey_preis_kwp,
    anlagenpreis,
    jahreserloes,
    opex,
    irr,
    rendite,
    payback,
    netto_erloes_25j,
    stromgestehungskosten,
    kapitalwert,
    cashflowTable,
    // Echo der Eingaben fuer Anzeige/Labels
    degradation,
    opexSteigerung,
    betrachtungszeitraum,
  };
}

/**
 * Berechnet die interne Rendite (IRR) per Newton-Raphson.
 *
 * @param {Array<{jaehrlich: number}>} cashflowTable - Jaehrliche Cashflows (ab Jahr 1)
 * @param {number} investition - Anfangsinvestition (positiver Wert)
 * @returns {number|null} IRR in Prozent oder null bei Nicht-Konvergenz
 */
function berechneIRR(cashflowTable, investition) {
  if (!cashflowTable.length || !investition) return null;

  // Cashflow-Array fuer IRR: Jahr 0 = -Investition, dann operative Cashflows (ohne Investition).
  // jaehrlich = erloes - betriebskosten - investition, wobei investition nur in Jahr 1 != 0.
  // Fuer IRR: cashflows[0] = -investition, cashflows[1..n] = erloes - betriebskosten
  const irrCashflows = [-investition];
  for (const row of cashflowTable) {
    irrCashflows.push(row.erloes - row.betriebskosten);
  }

  let rate = 0.1; // Startwert 10%
  const maxIter = 100;
  const tolerance = 1e-8;

  for (let iter = 0; iter < maxIter; iter++) {
    let npv = 0;
    let dnpv = 0; // Ableitung

    for (let i = 0; i < irrCashflows.length; i++) {
      const pv = irrCashflows[i] / Math.pow(1 + rate, i);
      npv += pv;
      if (i > 0) {
        dnpv -= i * irrCashflows[i] / Math.pow(1 + rate, i + 1);
      }
    }

    if (Math.abs(npv) < tolerance) {
      return Math.round(rate * 10000) / 100; // In Prozent mit 2 Nachkommastellen
    }

    if (Math.abs(dnpv) < 1e-14) {
      // Ableitung zu klein, kein Update moeglich
      return null;
    }

    const nextRate = rate - npv / dnpv;

    // Begrenzung auf sinnvollen Bereich
    if (nextRate < -0.99) rate = -0.5;
    else if (nextRate > 10) rate = 5;
    else rate = nextRate;
  }

  return null; // Nicht konvergiert
}
